import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { getTransformedMeetingType } from '../../../utils/meeting-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import CustomTable from '../../common/custom-table/CustomTable'
import CustomSwitch from '../../common/CustomSwitch'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import noMeetingsImg from '../../../assets/no-meetings.svg'
import { formatDate } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import CustomChip from '../../common/CustomChip'
import api from '../../../service/api'
import dayjs from 'dayjs'


const MeetingsList = () => {

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", filterDate: "", showScheduledOnly: "false", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByMeetingsCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const filterDate = searchParams.get( 'filterDate' ) || ""
    const showScheduledOnly = searchParams.get( 'showScheduledOnly' ) === 'true'
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [showDatePicker, setShowDatePicker] = useState( false )
    const anchorRef = useRef()

    const filtersExists = Boolean( searchText || ( selectedInstitute && selectedInstitute !== "all" ) || ( selectedDepartment && selectedDepartment !== "all" ) || filterDate || showScheduledOnly )

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [meetings, setMeetings] = useState( [] )

    const { layout, border, palette } = useTheme()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const UserStore = useUserStore()
    const isHybridClasses = pathname.includes( 'hybrid-classes' )

    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const handleDateFilterChange = ( e, newVal ) => {
        if ( newVal === '' ) {
            setSearchParamValue( 'filterDate', '' )
        }
    }

    const handleDateChange = newDate => {
        if ( newDate && newDate.isValid() ) {
            setSearchParamValue( 'filterDate', dayjs( newDate ).format( "YYYY-MM-DD" ) )
            setShowDatePicker( false )
        }
    }

    const handleClickOnRow = useCallback( ( row ) => {
        let url = `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}`

        const pathname = window.location.pathname

        url += pathname.includes( "online-classes" ) ? `/online-classes/${row.id}` : pathname.includes( "hybrid-classes" ) ? `/hybrid-classes/${row.id}` : `/meetings/${row.id}`
        navigate( url )
    }, [navigate, UserStore] )

    const handleViewStats = () => {
        let url = `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}`
        url += pathname.includes( "hybrid" ) ? `/hybrid-classes` : pathname.includes( "class" ) ? `/online-classes` : `/meetings`
        navigate( url )
    }

    const columns = useMemo( () => [
        { onCellClick: handleClickOnRow, field: "title", cellStyles: { textTransform: "capitalize" }, id: 2, name: "Meeting name", width: "250px", padding: "10px 20px" },
        { field: "faculty_name", cellStyles: { textTransform: "capitalize" }, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row?.creator_data.empcode}?legacy=${Boolean( row.legacy_system_data )}`, renderCell: row => row.creator_data.name?.toLowerCase(), id: 3, name: 'Faculty name', width: "150px", padding: "10px 20px" },
        { onCellClick: handleClickOnRow, field: "department", cellStyles: { textTransform: "capitalize" }, renderCell: row => row.creator_data?.department_name?.toLowerCase(), id: 6, name: 'Department', width: "250px", padding: "10px 20px" },
        { onCellClick: handleClickOnRow, hide: !pathname.includes( 'classes' ), field: "subject", id: 4, name: "Subject", renderCell: row => row.subject || "Not available", width: "200px", padding: "10px 20px" },
        { onCellClick: handleClickOnRow, field: "created_at", renderCell: row => formatDate( row.created_at ), id: 5, align: "center", name: "Created on", width: "200px", padding: "10px 20px" },
        { onCellClick: handleClickOnRow, field: "starts_at", renderCell: row => formatDate( row.starts_at, { format: "DD-MM-YYYY hh:mm A" } ), id: 7, align: "center", name: "Start(s/ed) At", width: "200px", padding: "10px 20px", styles: { textTransform: "initial" } },
        { onCellClick: handleClickOnRow, field: "attendance_fetched", renderCell: row => <CustomChip variant='filled' value={row?.attendance_fetched ? "Completed" : "Pending"} color={row?.attendance_fetched ? "success" : "secondary"} tooltipTitle={row?.attendance_fetched ? `Attendance synced at ${dayjs( row?.attendance_fetched_at ).format( "LLL" )}` : 'Attendance sync'} />, id: 7, align: "center", name: "Attendance Sync Status", width: "200px", padding: "10px 20px" }
    ], [handleClickOnRow, pathname] )

    const filterFunction = useCallback( item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( +department_id === +item.creator_data.department_id )
            if ( !doesMatchDepartment ) return false
        }

        if ( isHybridClasses ) {
            if ( showScheduledOnly && !dayjs( item.ends_at || item.starts_at ).isAfter( dayjs() ) ) return false

            if ( filterDate?.match( /^(\d{4}-\d{2}-\d{2})$/ ) ) {
                if ( !dayjs( item.starts_at ).isSame( dayjs( filterDate ), 'day' ) ) return false
            }
        }

        return item.creator_data.department_name?.match( searchValue ) || item.creator_data?.name?.match( searchValue ) || item.created_by?.match( searchValue )
    }, [searchText, selectedDepartment, isHybridClasses, showScheduledOnly, filterDate] )


    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'filterDate', '' )
            prev.set( 'showScheduledOnly', 'false' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const filteredList = useMemo( () => {
        return meetings.filter( filterFunction )
    }, [meetings, filterFunction] )

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getMeetingsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "", types: pathname.includes( 'online-classes' ) ? ["class"] : pathname.includes( 'hybrid-classes' ) ? ["hybrid-class"] : ["webinar", "discussion"] } )

            setMeetings( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.message?.data?.message || "Something went wrong while fetching meetings." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute, pathname] )


    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Box overflow="auto" gap={layout.cardsGap} minHeight="450px" flexGrow={1} display="flex" flexDirection="column" >
            <Box display="flex" gap="20px" alignItems="flex-end" justifyContent="space-between" flexWrap="wrap">
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="16px">{isHybridClasses ? "Hybrid classes" : pathname.match( /class/ ) ? "Online classes" : "Meetings"}</Typography>
                <Box position="relative" flexWrap="wrap" display="flex" gap="20px" alignItems="flex-end" >
                    {isHybridClasses && <>
                        <Box display="flex" alignItems="center" gap="10px">
                            <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Scheduled only</Typography>
                            <CustomSwitch size='small' checked={showScheduledOnly} onChange={e => setSearchParamValue( 'showScheduledOnly', e.target.checked ? 'true' : 'false' )} />
                        </Box>

                        <ToggleButtonGroup size='small' value={filterDate || ""} onChange={handleDateFilterChange} exclusive >
                            <ToggleButton size='small' sx={{ textTransform: "capitalize", padding: "4.5px 10px" }} value="">All</ToggleButton>
                            <ToggleButton
                                ref={anchorRef}
                                size="small"
                                sx={{
                                    textTransform: "capitalize",
                                    padding: "4.5px 10px",
                                    cursor: "pointer"
                                }}
                                value="-"
                                selected={Boolean( filterDate.match( /^(\d{4}-\d{2}-\d{2})$/ ) )}
                                onClick={() => setShowDatePicker( true )}
                            >
                                {filterDate || "Select date"}
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                open={showDatePicker}
                                onClose={() => setShowDatePicker( false )}
                                views={["year", "month", "day"]}
                                value={filterDate ? dayjs( filterDate ) : null}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        component: "div",
                                        sx: {
                                            display: "none"
                                        }
                                    },
                                    popper: {
                                        anchorEl: anchorRef.current,
                                        placement: "bottom-start"
                                    }
                                }}
                                slots={{
                                    openPickerButton: null
                                }}
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0,
                                    pointerEvents: "none"
                                }}
                            />
                        </LocalizationProvider>
                    </>}
                    <Button size="small" onClick={handleViewStats} variant="contained" color='secondary' disableElevation sx={{ height: "33.5px", textTransform: "capitalize", }} >View Stats</Button>
                </Box>
            </Box >

            {!loading && !error && filteredList.length > 0 && <CustomTable
                globalSearch="Search by Meeting title, faculty name, faculty ID or subject."
                columns={columns}
                data={filteredList}
                pageSize={50}
                containerStyles={{
                    overflow: "hidden", border: border[1], borderRadius: "5px"
                }}
            />}
            {!loading && !error && filteredList.length === 0 && <Box flexDirection="column" padding="20px" display="flex" bgcolor={palette.contentBg} borderRadius={layout.borderRadius} alignItems="center" justifyContent="center" gap="20px">
                <Box component="img" src={noMeetingsImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No {getTransformedMeetingType( pathname.includes( "hybrid" ) ? `hybrid-class` : pathname.includes( "class" ) ? `class` : `discussion`, { plural: true, forDisplay: true } )} were found for applied filters!</Typography>
                {filtersExists && <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>}
            </Box>}
            {loading && !error && <DataLoadingSpinner waitingMessage="Loading meetings please wait...." />}
            {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} loading={loading} />}
        </Box>
    )
}



/* 
<TableRow sx={{ cursor: "pointer", ":hover": { background: palette.rowHover } }} className='table-row' key={row.assignment_id}>
                                    <TableCell onClick={() => handleClickOnRow( row )} sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color, textTransform: "capitalize" }}><strong>{row.title}</strong></TableCell>
                                    <TableCell align={columns[1].align || "left"} sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}><Typography fontSize="12px" sx={{ '&:hover': { textDecoration: "underline" } }}><strong><Link to={`/${window.location.pathname?.includes('admin') ? "admin" : "audit"}/faculty/${row?.creator_data.empcode}?legacy=${Boolean( row.legacy_system_data )}`} className='default-link' >{row.creator_data.name?.toLowerCase()}</Link></strong></Typography></TableCell>
                                    <TableCell onClick={() => handleClickOnRow( row )} align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}><strong>{row.creator_data?.department_name?.toLowerCase()}</strong></TableCell>
                                    <TableCell onClick={() => handleClickOnRow( row )} align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}><strong>{row.subject || "Not available"}</strong></TableCell>
                                    <TableCell onClick={() => handleClickOnRow( row )} align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}><strong>{formatDate( row.created_at )}</strong></TableCell>
                                    <TableCell onClick={() => handleClickOnRow( row )} align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[5].padding || "10px 20px", color: table.color }}><strong>{formatDate( row.starts_at, { format: "DD-MM-YYYY hh:mm A" } )}</strong></TableCell>
                                </TableRow>

*/


export default MeetingsList
